import {
    PaginationFactory,
    PaginatorFactory
} from "../factory";
import { store } from "../store";
import { ScheduleType } from "../types/schedule.types";
import { api } from "../http";
import { DefaultRes } from "../types";
import { AxiosError, AxiosResponse } from "axios";
import { RESPONSE_CODES } from "../constants/responseCodes.constants";

export class ScheduleService {
    public static readonly dispatch = store.dispatch.schedule;
    public static readonly state = store.getState();

    public static async fetchMany(
        dto: PaginatorFactory<{}>,
        callback?: (isValid?: boolean, message?: string) => void,
        ignoreState = false
    ): Promise<PaginationFactory<ScheduleType.Model> | void> {
        const {
            dispatch: {
                toggleLoading,
                setPagination,
            },
        } = ScheduleService;

        if (!ignoreState) {
            toggleLoading("fetchMany", true);
        }

        try {

            const {
                data: {
                    result: { items, page, perPage, total },
                    isValid,
                    message,
                },
            } = await api.post<
                DefaultRes<PaginationFactory<ScheduleType.Model>>,
                AxiosResponse<DefaultRes<PaginationFactory<ScheduleType.Model>>>
            >(`schedules/list-schedules`);

            if (!ignoreState) {
                toggleLoading("fetchMany", false);

                if (isValid) {
                    setPagination({ items, page, perPage, total });
                }
            }

            callback && callback(isValid, RESPONSE_CODES[message]);

            return { items, page, perPage, total };
        } catch (error: any) {
            toggleLoading("fetchMany", false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<ScheduleType.Model>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }

            callback && callback(false, String(error));
        }
    }

    /**
 * Updates a schedule by calling "schedules/update-schedule" endpoint.
 * @param schedule The schedule object to be updated.
 * @param callback Optional callback function for handling response.
 * @returns The updated schedule object if successful.
 */
    public static async updateSchedule(
        schedule: ScheduleType.Model,
        callback?: (isValid?: boolean, message?: string) => void
    ): Promise<ScheduleType.Model | void> {
        const {
            dispatch: {
                toggleLoading,
                setPagination
            },
        } = ScheduleService;
        toggleLoading("updateSchedule", true);
        try {
            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<ScheduleType.Model>,
                AxiosResponse<DefaultRes<ScheduleType.Model>>
            >(`schedules/update-schedule`, {
                _id: schedule._id,
                optimizedBlocks: schedule.optimizedBlocks,
                timezone: schedule.timezone,
                teamId: schedule.teamId,
            });

            toggleLoading("updateSchedule", false);

            if (isValid) {
                const state = store.getState();

                // Handle case where pagination.items might be undefined (when loading single schedule)
                if (state.schedule.pagination.items) {
                    const newItems = state.schedule.pagination.items.map((item) =>
                        result._id === item._id ? result : item
                    )
                    setPagination({
                        items: newItems,
                        page: state.schedule.pagination.page,
                        perPage: state.schedule.pagination.perPage,
                        total: state.schedule.pagination.total,
                    });
                }

                // Set the updated schedule as the selected schedule
                this.setSchedule(result);
            }
            callback && callback(isValid, RESPONSE_CODES[message]);

            return result;
        } catch (error: any) {
            console.log(error)
            toggleLoading("updateSchedule", false);

            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<ScheduleType.Model>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }

            callback && callback(false, String(error));
        }
    }

    /**
    * Sets the selected schedule in the store.
    * @param schedule The schedule object to set.
    */
    public static setSchedule(schedule: ScheduleType.Model | null) {
        const {
            dispatch: { setSelectedSchedule },
        } = ScheduleService;
        if (schedule != null) {
            const newSchedule: ScheduleType.Model = structuredClone(schedule);
            setSelectedSchedule(newSchedule);
        } else {
            setSelectedSchedule(null)
        }
    }

    /**
     * Creates a new empty schedule by calling "schedules/create-schedule" endpoint.
     * @param callback Optional callback function for handling response.
     * @returns The newly created schedule if successful.
     */
    public static async createSchedule(teamId: string,
        callback?: (isValid?: boolean, message?: string) => void
    ): Promise<ScheduleType.Model | void> {
        const {
            dispatch: {
                toggleLoading,
                setPagination
            },
        } = ScheduleService;
        toggleLoading("createSchedule", true);
        try {
            const emptySchedule: Partial<ScheduleType.Model> = {
                teamId: teamId,
                startDay: 1,
                timezone: 'America/New_York',
            };
            const {
                data: result,
                status,
            } = await api.post<
                DefaultRes<ScheduleType.Model>,
                AxiosResponse<ScheduleType.Model>
            >(`schedules/create-schedule`, emptySchedule);

            toggleLoading("createSchedule", false);
            const isValid = status === 200
            if (isValid) {
                const state = store.getState();
                const newItems = [...(state.schedule.pagination.items ?? []), result];
                setPagination({
                    items: newItems,
                    page: state.schedule.pagination.page,
                    perPage: state.schedule.pagination.perPage,
                    total: state.schedule.pagination.total + 1,
                });
                this.setSchedule(result);
            }
            callback && callback(isValid);

            return result;
        } catch (error: any) {
            console.log(error)
            toggleLoading("createSchedule", false);
            if (error.response) {
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<ScheduleType.Model>>
                ).response?.data?.message;

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || RESPONSE_CODES.UNKNOWN);
                return;
            }

            callback && callback(false, String(error));
        }
    }

    /**
 * Fetches a single schedule by ID by calling "schedules/get-schedule/:id" endpoint.
 * @param id The ID of the schedule to fetch.
 * @param callback Optional callback function for handling response.
 * @returns The fetched schedule if successful.
 */
    public static async getScheduleById(
        id: string,
        callback?: (isValid?: boolean, message?: string) => void
    ): Promise<ScheduleType.Model | void> {
        const {
            dispatch: { toggleLoading },
        } = ScheduleService;

        toggleLoading("fetchById", true);

        try {
            const {
                data: { result, isValid, message },
            } = await api.post<
                DefaultRes<ScheduleType.Model>,
                AxiosResponse<DefaultRes<ScheduleType.Model>>
            >(`schedules/get-schedule/${id}`);

            toggleLoading("fetchById", false);

            if (isValid) {
                this.setSchedule(result);
            }
            callback && callback(isValid, RESPONSE_CODES[message]);
            return result;
        } catch (error: any) {
            toggleLoading("fetchById", false);

            // Don't log errors for schedule fetches - it's normal for new teams to not have schedules
            if (error.response) {
                const status = error.response.status;
                const ERROR_CODE = (
                    error as AxiosError<DefaultRes<ScheduleType.Model>>
                ).response?.data?.message;

                // Only log actual errors, not 404s or 500s from missing schedules
                if (status !== 404 && status !== 500) {
                    console.log("Schedule fetch error:", error);
                }

                callback && callback(false, RESPONSE_CODES[ERROR_CODE!] || 'Schedule not found');
            } else {
                console.log("Network error fetching schedule:", error);
                callback && callback(false, String(error));
            }
        }
    }

}